import { useState } from "react";
import { registerAgency } from "../../api/auth";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Stack,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { Link } from "react-router-dom";
import { REGIONS, getStatesByCountry, LAGOS_ZONES } from "../../lib/regions";
import Logo from "../../assets/png.png";

export default function SignupPage() {
  const [form, setForm] = useState({
    agencyName: "",
    country: "",
    state: "",
    city: "",
    email: "",
    password: "",
    phone: "",
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const states = form.country ? getStatesByCountry(form.country) : [];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "country" ? { state: "", city: "" } : {}),
      ...(name === "state" ? { city: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSuccess("");
    setError("");
    try {
      const res = await registerAgency(form);
      setSuccess(res.data.message || "Registration successful! Please check your email.");
    } catch (err) {
      setError(err.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      p={2}
    >
      <Box
        component="img"
        src={Logo}
        alt="Client Portal"
        sx={{
          width: "100%",
          maxWidth: 220,
          height: "auto",
          objectFit: "contain",
          display: "block",
          mb: 3,
        }}
      />

      <Paper elevation={3} sx={{ p: 4, width: 440 }}>
        <Box textAlign="center" mb={2.5}>
          <Typography variant="h5">Register Agency</Typography>
        </Box>
        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              name="agencyName"
              label="Agency Name"
              value={form.agencyName}
              onChange={handleChange}
              fullWidth
              required
            />

            <FormControl fullWidth required>
              <InputLabel>Country</InputLabel>
              <Select
                name="country"
                value={form.country}
                label="Country"
                onChange={handleChange}
              >
                {Object.entries(REGIONS).map(([region, countries]) => [
                  <MenuItem key={`header-${region}`} disabled sx={{ fontWeight: 700, opacity: 1, color: "#64748b", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1 }}>
                    {region}
                  </MenuItem>,
                  ...Object.keys(countries).map((country) => (
                    <MenuItem key={country} value={country} sx={{ pl: 3 }}>
                      {country}
                    </MenuItem>
                  )),
                ])}
              </Select>
            </FormControl>

            <FormControl fullWidth required disabled={!form.country}>
              <InputLabel>State / Province / Region</InputLabel>
              <Select
                name="state"
                value={form.state}
                label="State / Province / Region"
                onChange={handleChange}
              >
                {states.map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {form.state === "Lagos" && (
              <FormControl fullWidth required>
                <InputLabel>City / Area</InputLabel>
                <Select
                  name="city"
                  value={form.city}
                  label="City / Area"
                  onChange={handleChange}
                >
                  {Object.entries(LAGOS_ZONES).map(([zone, cities]) => [
                    <MenuItem key={`header-${zone}`} disabled sx={{ fontWeight: 700, opacity: 1, color: "#64748b", fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: 1 }}>
                      {zone}
                    </MenuItem>,
                    ...cities.map((city) => (
                      <MenuItem key={city} value={city} sx={{ pl: 3 }}>
                        {city}
                      </MenuItem>
                    )),
                  ])}
                </Select>
              </FormControl>
            )}

            <TextField
              name="email"
              label="Email"
              value={form.email}
              onChange={handleChange}
              type="email"
              fullWidth
              required
            />
            <TextField
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={handleChange}
              fullWidth
              required
            />
            <TextField
              name="password"
              label="Password"
              value={form.password}
              onChange={handleChange}
              type="password"
              fullWidth
              required
            />

            <Button type="submit" variant="contained" disabled={loading}>
              {loading ? "Submitting..." : "Register"}
            </Button>
            <Typography variant="body2" textAlign="center">
              Already have an account? <Link to="/">Login</Link>
            </Typography>
          </Stack>
        </form>
        {success && <Alert severity="success" sx={{ mt: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Paper>
    </Box>
  );
}